/**
 * Déverrouillage par passkey (WebAuthn PRF) — RACCOURCI LOCAL, jamais une
 * racine (`docs/ARCHITECTURE.md` §1, §4).
 *
 * ```
 * prfOutput            = WebAuthn prf.eval(salt) sur l'authentificateur (32 o, secret)
 * passkeyStretchedKey  = HKDF-SHA256(prfOutput, info="…|passkey-stretched-key")
 * passkeyProtectedKey  = seal(passkeyStretchedKey, vaultKey, contexte="…|passkey-protected-symmetric-key")
 * ```
 *
 * ⚠️ CE QUE CE MODULE NE CHANGE PAS. Le mot de passe maître reste la racine
 * NON DÉLÉGABLE : il scelle la `vaultKey` de son côté (`protected-key.ts`), et
 * cette voie-là n'est ni retirée ni affaiblie. La passkey n'ouvre qu'une COPIE
 * scellée de la même `vaultKey`, révocable en supprimant ce blob — et rien
 * d'autre. Perdre la passkey ne perd pas le coffre ; révoquer la passkey ne
 * touche pas au coffre.
 *
 * Comme la clé de récupération, la sortie PRF n'est PAS étirée par Argon2id :
 * ses 256 bits viennent de l'authentificateur (clé HMAC matérielle,
 * inextractible, évaluée seulement après vérification de l'utilisateur), donc
 * une attaque hors ligne sur le blob n'a aucun dictionnaire à parcourir. Le
 * HKDF ne sert qu'à la séparation de domaine.
 */
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import {
  KDF_DOMAIN,
  PASSKEY_PRF_OUTPUT_LENGTH,
  PASSKEY_PRF_SALT_LENGTH,
  SEAL_CONTEXT,
  SYMMETRIC_KEY_LENGTH,
  type AeadAlgorithmId,
} from './constants.js';
import { openFromString, sealToString, type SealedString } from './aead.js';
import { utf8ToBytes, wipe } from './bytes.js';
import { InvalidParameterError } from './errors.js';
import { randomBytes } from './random.js';
import { SecretKey, assertSecretKey } from './secret.js';

/** `vaultKey` scellée sous la sortie PRF d'une passkey. Ne quitte JAMAIS l'appareil. */
export type PasskeyProtectedSymmetricKey = SealedString;

/**
 * Sel de l'évaluation PRF (`eval.first`), un par passkey enrôlée. Donnée
 * PUBLIQUE : elle est stockée en clair et n'a pas à être protégée — c'est la
 * clé HMAC de l'authentificateur, qu'elle ne quitte jamais, qui fait le secret.
 */
export function generatePasskeyPrfSalt(): Uint8Array {
  return randomBytes(PASSKEY_PRF_SALT_LENGTH);
}

/**
 * Enveloppe la sortie PRF renvoyée par l'authentificateur. Le tampon d'entrée
 * est ADOPTÉ (il sera effacé par `destroy()`) : l'appelant ne doit plus le lire.
 *
 * @throws {InvalidParameterError} longueur inattendue (la spécification WebAuthn
 *   fixe 32 octets : autre chose signale un authentificateur ou un pont douteux)
 */
export function adoptPasskeyPrfOutput(prfOutput: Uint8Array): SecretKey {
  if (!(prfOutput instanceof Uint8Array) || prfOutput.length !== PASSKEY_PRF_OUTPUT_LENGTH) {
    const taille = prfOutput instanceof Uint8Array ? String(prfOutput.length) : 'aucun';
    throw new InvalidParameterError(
      `prfOutput: ${PASSKEY_PRF_OUTPUT_LENGTH} octets attendus, ${taille} recus`,
    );
  }
  return SecretKey.adopt(prfOutput, 'passkeyPrfOutput', PASSKEY_PRF_OUTPUT_LENGTH);
}

/**
 * `passkeyStretchedKey = HKDF-SHA256(prfOutput, info="…|passkey-stretched-key")`.
 * Clé de scellement de la `vaultKey` par la voie passkey.
 */
export function derivePasskeyStretchedKey(prfOutput: SecretKey): SecretKey {
  assertSecretKey(prfOutput, 'prfOutput', PASSKEY_PRF_OUTPUT_LENGTH);
  const bytes = hkdf(
    sha256,
    prfOutput.reveal(),
    undefined,
    utf8ToBytes(KDF_DOMAIN.passkeyStretchedKey),
    SYMMETRIC_KEY_LENGTH,
  );
  return SecretKey.adopt(bytes, 'passkeyStretchedKey', SYMMETRIC_KEY_LENGTH);
}

export interface ProtectVaultKeyWithPasskeyInput {
  readonly vaultKey: SecretKey;
  readonly prfOutput: SecretKey;
  readonly algorithm?: AeadAlgorithmId;
}

/** Scelle la `vaultKey` sous la sortie PRF de la passkey (nonce frais). */
export function protectVaultKeyWithPasskey(
  input: ProtectVaultKeyWithPasskeyInput,
): PasskeyProtectedSymmetricKey {
  assertSecretKey(input?.vaultKey, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  assertSecretKey(input.prfOutput, 'prfOutput', PASSKEY_PRF_OUTPUT_LENGTH);
  const stretched = derivePasskeyStretchedKey(input.prfOutput);
  try {
    return sealToString(stretched, input.vaultKey.reveal(), {
      context: SEAL_CONTEXT.passkeyProtectedSymmetricKey,
      algorithm: input.algorithm,
    });
  } finally {
    stretched.destroy();
  }
}

export interface UnprotectVaultKeyWithPasskeyInput {
  readonly protectedKey: PasskeyProtectedSymmetricKey;
  readonly prfOutput: SecretKey;
}

/**
 * Ouvre la `vaultKey` scellée sous la passkey.
 *
 * @throws {DecryptionFailedError} mauvaise passkey, mauvais sel, ou blob altéré
 *   (un blob substitué par un attaquant échoue ici : le contexte AEAD interdit
 *   aussi de présenter le blob d'une AUTRE voie, mot de passe ou récupération)
 * @throws {MalformedCiphertextError} conteneur structurellement invalide
 */
export function unprotectVaultKeyWithPasskey(input: UnprotectVaultKeyWithPasskeyInput): SecretKey {
  assertSecretKey(input?.prfOutput, 'prfOutput', PASSKEY_PRF_OUTPUT_LENGTH);
  if (typeof input.protectedKey !== 'string' || input.protectedKey.length === 0) {
    throw new InvalidParameterError('protectedKey: passkey protected symmetric key manquante');
  }
  const stretched = derivePasskeyStretchedKey(input.prfOutput);
  try {
    const bytes = openFromString(stretched, input.protectedKey, {
      context: SEAL_CONTEXT.passkeyProtectedSymmetricKey,
    });
    if (bytes.length !== SYMMETRIC_KEY_LENGTH) {
      wipe(bytes);
      throw new InvalidParameterError('vaultKey dechiffree de longueur inattendue');
    }
    return SecretKey.adopt(bytes, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  } finally {
    stretched.destroy();
  }
}
